/**
 * Every path pattern the signed-in app can render: the AuthenticatedApp
 * switch in App.tsx plus AdminRoutes in admin-routes.tsx.
 *
 * The public router needs the complement. A URL that is neither a
 * marketing/legal route (matched earlier in App.tsx) nor one of these is a
 * dead link, and must get the public not-found page rather than a bounce to
 * the sign-in form. Patterns use the wouter syntax the app uses: literal
 * segments, `:param` segments, and a trailing `/*?` wildcard. Matching mirrors
 * wouter's regexparam: case-insensitive, optional trailing slash.
 *
 * app-routes.test.ts reads both route files and fails when a route is added
 * there without being listed here (or listed here without existing there).
 *
 * Two of the app's master kill switches remove routes from the router at
 * build time (App.tsx and admin-routes.tsx read the same variables), so the
 * table is derived from the same flags: with VITE_AD_MARKETPLACE_ENABLED=false
 * (the Dockerfile and deploy defaults) /marketplace is not routed, and must
 * get the public not-found page rather than a bounce to sign-in that lands
 * on the in-app NotFound.
 */

/** Build-time kill switches that remove routes from the router. */
export interface AppRouteFlags {
  /** VITE_AD_MARKETPLACE_ENABLED !== "false" (App.tsx, admin-routes.tsx). */
  adMarketplace: boolean;
  /** VITE_DEVICE_HARDWARE_ENABLED !== "false" (App.tsx). */
  deviceHardware: boolean;
}

/** Routes that exist only while the Ad Marketplace switch is on. */
export const AD_MARKETPLACE_ROUTE_PATTERNS: readonly string[] = [
  "/marketplace/*?",
  "/admin/marketplace",
  "/ad-rental",
  "/advertise",
];

/** Routes that exist only while the device-hardware switch is on. */
export const DEVICE_HARDWARE_ROUTE_PATTERNS: readonly string[] = [
  "/admin/inventory",
  "/admin/orders",
];

/** Every route the signed-in app can render with all switches on. */
export const ALL_APP_ROUTE_PATTERNS: readonly string[] = [
  // App.tsx, AuthenticatedApp
  "/start",
  "/home",
  "/workspaces",
  "/marketplace/*?",
  "/admin/marketplace",
  "/admin/inventory",
  "/admin/orders",
  "/admin/growth",
  "/admin/customers",
  "/admin",
  "/admin/fleet",
  "/admin/support",
  "/admin/prospects",
  "/admin/audit",
  "/player-selection",
  "/player-companion",
  "/control-selection",
  "/control/:deviceId",
  // admin-routes.tsx, AdminRoutes
  "/dashboard",
  "/onboarding",
  "/sites",
  "/schedules",
  "/campaigns",
  "/audiences",
  "/fleet/:deviceId",
  "/fleet",
  "/locations",
  "/library/music",
  "/library/announcements",
  "/library/ads",
  "/library/screen",
  "/library",
  "/media",
  "/catalogue",
  "/playlists/:id",
  "/playlists",
  "/channels",
  "/broadcast",
  "/scheduling/:id",
  "/scheduling",
  "/team",
  "/billing",
  "/ad-rental",
  "/advertise",
  "/settings",
  "/account",
  "/logs",
  "/reports",
  "/support",
  "/install",
];

/** The routes a build with the given switches actually registers. */
export function appRoutePatternsFor(flags: AppRouteFlags): readonly string[] {
  return ALL_APP_ROUTE_PATTERNS.filter(
    (p) =>
      (flags.adMarketplace || !AD_MARKETPLACE_ROUTE_PATTERNS.includes(p)) &&
      (flags.deviceHardware || !DEVICE_HARDWARE_ROUTE_PATTERNS.includes(p)),
  );
}

/**
 * The switches as the router reads them. `import.meta.env` is Vite's build-time
 * object; outside Vite (node --test) it is undefined, which reads as every
 * switch on, the same as an unset variable.
 */
export function appRouteFlagsFromEnv(
  env: Record<string, string | undefined> = readEnv(),
): AppRouteFlags {
  return {
    adMarketplace: env.VITE_AD_MARKETPLACE_ENABLED !== "false",
    deviceHardware: env.VITE_DEVICE_HARDWARE_ENABLED !== "false",
  };
}

function readEnv(): Record<string, string | undefined> {
  try {
    const env = import.meta.env as Record<string, string | undefined> | undefined;
    return env ?? {};
  } catch {
    return {};
  }
}

/** The routes THIS build registers. */
export const APP_ROUTE_PATTERNS: readonly string[] = appRoutePatternsFor(
  appRouteFlagsFromEnv(),
);

function toRegExp(pattern: string): RegExp {
  const source = pattern
    .replace(/:[A-Za-z0-9_]+/g, "[^/]+")
    .replace(/\/\*\?$/, "(?:/.*)?");
  return new RegExp(`^${source}/?$`, "i");
}

const APP_ROUTE_REGEXPS = APP_ROUTE_PATTERNS.map(toRegExp);

/**
 * True when a wouter location (pathname with the router base already
 * stripped; a query string or hash is ignored) is one of the app's routes.
 * `patterns` defaults to this build's table; tests pass another build's.
 */
export function isAppRoutePath(
  location: string,
  patterns: readonly string[] = APP_ROUTE_PATTERNS,
): boolean {
  const pathname = location.split(/[?#]/, 1)[0] ?? location;
  const regexps =
    patterns === APP_ROUTE_PATTERNS ? APP_ROUTE_REGEXPS : patterns.map(toRegExp);
  return regexps.some((re) => re.test(pathname));
}
